import React from 'react';

/**
 * YEGO Refined Brand Logo
 * Features:
 * - Ambient Backlight Glow Layer (radiant emerald-gold illumination)
 * - Sculptural "Y" Crest (Thousand Hills Emerald & Radiant Teal)
 * - Kigali Sunburst Beacon (Rwanda golden dawn accent)
 * - Precision vector geometry optimized for high-density screens
 */
const YegoLogo = ({ size = 40, className = '', withBacklight = true }) => {
  const id = React.useId();
  
  return (
    <div 
      className={`relative flex-shrink-0 flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Ambient Backlight Glow Halo */}
      {withBacklight && (
        <div 
          className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-emerald-500/40 via-teal-400/30 to-amber-400/25 blur-[7px] pointer-events-none transform transition-all duration-300 group-hover:scale-125 group-hover:blur-[10px] group-hover:from-emerald-500/50 group-hover:to-amber-400/35"
          style={{ willChange: 'filter, transform' }}
        />
      )}

      <svg
        viewBox="0 0 100 100"
        className="w-full h-full relative z-10 transition-transform duration-300 group-hover:scale-105"
        style={{ filter: 'drop-shadow(0 2px 5px rgba(4, 120, 87, 0.18))' }}
      >
        <defs>
          {/* Squircle Tile Background - Light Mode */}
          <linearGradient id={`${id}-squircle-light`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.98" />
            <stop offset="60%" stopColor="#f8fafc" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#ecfdf5" stopOpacity="0.92" />
          </linearGradient>

          {/* Squircle Tile Background - Dark Mode */}
          <linearGradient id={`${id}-squircle-dark`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e293b" stopOpacity="0.98" />
            <stop offset="60%" stopColor="#0f172a" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#064e3b" stopOpacity="0.85" />
          </linearGradient>

          {/* Left Wing - Thousand Hills Emerald */}
          <linearGradient id={`${id}-emerald-wing`} x1="20%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="40%" stopColor="#10b981" />
            <stop offset="85%" stopColor="#059669" />
            <stop offset="100%" stopColor="#047857" />
          </linearGradient>

          {/* Right Wing - Vibrant Teal */}
          <linearGradient id={`${id}-teal-wing`} x1="80%" y1="0%" x2="20%" y2="100%">
            <stop offset="0%" stopColor="#5eead4" />
            <stop offset="40%" stopColor="#14b8a6" />
            <stop offset="85%" stopColor="#0d9488" />
            <stop offset="100%" stopColor="#065f46" />
          </linearGradient>

          {/* Center Pillar / Stem */}
          <linearGradient id={`${id}-stem-gradient`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#059669" />
            <stop offset="50%" stopColor="#047857" />
            <stop offset="100%" stopColor="#064e3b" />
          </linearGradient>

          {/* Sunburst Beacon - Rwanda Golden Dawn */}
          <radialGradient id={`${id}-sun-beacon`} cx="38%" cy="32%" r="68%">
            <stop offset="0%" stopColor="#fffbeb" />
            <stop offset="30%" stopColor="#fde047" />
            <stop offset="70%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#d97706" />
          </radialGradient>

          {/* Sun Halo / Aura */}
          <radialGradient id={`${id}-sun-aura`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.5" />
            <stop offset="60%" stopColor="#10b981" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </radialGradient>

          {/* Glass Specular Highlight */}
          <linearGradient id={`${id}-glass-shine`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.75" />
            <stop offset="40%" stopColor="#ffffff" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>

          {/* Rim Border Stroke */}
          <linearGradient id={`${id}-rim-gradient`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#047857" stopOpacity="0.3" />
          </linearGradient>

          {/* Soft Drop Shadow for Facets */}
          <filter id={`${id}-facet-shadow`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#047857" floodOpacity="0.25" />
          </filter>
        </defs>

        {/* Outer Squircle Container */}
        <rect
          x="6"
          y="6"
          width="88"
          height="88"
          rx="26"
          className="fill-[url(#squircle-light)] dark:fill-[url(#squircle-dark)]"
          style={{ fill: `url(#${id}-squircle-light)` }}
        />

        {/* Subtle Frosted Specular Sheen on Tile */}
        <path
          d="M 6 32 C 6 17.6, 17.6 6, 32 6 L 68 6 C 82.4 6, 94 17.6, 94 32 L 94 48 C 65 38, 35 44, 6 58 Z"
          fill={`url(#${id}-glass-shine)`}
          className="pointer-events-none"
        />

        {/* Squircle Precision Rim Border */}
        <rect
          x="6"
          y="6"
          width="88"
          height="88"
          rx="26"
          fill="none"
          stroke={`url(#${id}-rim-gradient)`}
          strokeWidth="1.8"
          className="pointer-events-none"
        />

        {/* Sun Beacon Backlight Aura in Upper Cradle */}
        <circle
          cx="50"
          cy="34"
          r="20"
          fill={`url(#${id}-sun-aura)`}
          className="pointer-events-none animate-pulse"
          style={{ animationDuration: '3s' }}
        />

        {/* Sunburst Golden Orb */}
        <g filter={`url(#${id}-facet-shadow)`}>
          <circle
            cx="50"
            cy="34"
            r="9.5"
            fill={`url(#${id}-sun-beacon)`}
          />
          {/* Sun Specular Highlight */}
          <circle
            cx="47.5"
            cy="31.5"
            r="3"
            fill="#ffffff"
            opacity="0.8"
          />
        </g>

        {/* Central Faceted "Y" Monogram Crest */}
        <g filter={`url(#${id}-facet-shadow)`}>
          {/* Grounded Center Stem */}
          <path
            d="M 44.5 50 L 55.5 50 L 55.5 76 C 55.5 78.5, 53.5 80, 50 80 C 46.5 80, 44.5 78.5, 44.5 76 Z"
            fill={`url(#${id}-stem-gradient)`}
          />

          {/* Left Wing (Rising Hill Curve) */}
          <path
            d="M 21 24 C 23.5 24, 26 25, 28 27 L 48 53 L 44 56 L 19 31 C 18 28.5, 18.5 25.5, 21 24 Z"
            fill={`url(#${id}-emerald-wing)`}
          />

          {/* Right Wing (Dawn Light Horizon) */}
          <path
            d="M 79 24 C 76.5 24, 74 25, 72 27 L 52 53 L 56 56 L 81 31 C 82 28.5, 81.5 25.5, 79 24 Z"
            fill={`url(#${id}-teal-wing)`}
          />

          {/* Central Convergence Diamond Node */}
          <polygon
            points="50,44 56,51 50,58 44,51"
            fill="#ffffff"
            opacity="0.9"
          />
          <polygon
            points="50,46 54,51 50,56 46,51"
            fill="#34d399"
            opacity="0.6"
          />
        </g>
      </svg>
    </div>
  );
};

export default YegoLogo;
